import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProgram, calculateLoan, getProgramRecommendations } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { EdFiLogo } from '../components/EdFiLogo';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clock, DollarSign, Building2, ArrowLeft, Calculator, CheckCircle, Globe, Star } from 'lucide-react';

const ProgramDetailPage = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [termMonths, setTermMonths] = useState('12');
  const [loanCalculation, setLoanCalculation] = useState(null);
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const [progRes, recsRes] = await Promise.all([
          getProgram(programId),
          getProgramRecommendations(programId).catch(() => ({ data: { recommendations: [] } }))
        ]);
        setProgram(progRes.data);
        setRecommendations(recsRes.data.recommendations || []);
      } catch (error) {
        console.error('Error fetching program:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgram();
  }, [programId]);

  useEffect(() => {
    if (program) {
      const calculate = async () => {
        try {
          const downPayment = program.price * 0.1;
          const amount = program.price - downPayment;
          const response = await calculateLoan({
            amount,
            term_months: parseInt(termMonths),
            scoring: 'promedio'
          });
          setLoanCalculation(response.data);
        } catch (error) {
          console.error('Error calculating loan:', error);
        }
      };
      calculate();
    }
  }, [program, termMonths]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getModalityLabel = (modality) => {
    const labels = {
      'online': 'En línea',
      'presencial': 'Presencial',
      'hibrido': 'Híbrido',
      'virtual': 'Virtual'
    };
    return labels[modality] || modality;
  };

  const handleApply = () => {
    if (isAuthenticated) {
      navigate(`/apply/${programId}`);
    } else {
      navigate('/register', { state: { redirectTo: `/apply/${programId}` } });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Programa no encontrado</h2>
          <Link to="/programs">
            <Button>Ver todos los programas</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <EdFiLogo size="sm" linkTo="/" />
              <Link to="/programs" className="text-slate-600 hover:text-brand-blue transition-colors flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Programas
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-brand-blue/10 text-brand-blue">
                  {program.category}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600">
                  <Globe className="w-4 h-4 mr-1" />
                  {getModalityLabel(program.modality)}
                </span>
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" data-testid="program-title">
                {program.name}
              </h1>

              {program.university && (
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-brand-blue rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{program.university.name}</p>
                    <p className="text-sm text-slate-600">{program.university.description?.slice(0, 60)}...</p>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Clock className="w-5 h-5" />
                    <span className="text-sm">Duración</span>
                  </div>
                  <p className="text-xl font-bold text-slate-900">{program.duration_months} meses</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <DollarSign className="w-5 h-5" />
                    <span className="text-sm">Inversión</span>
                  </div>
                  <p className="text-xl font-bold text-slate-900">{formatPrice(program.price)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Calculator className="w-5 h-5" />
                    <span className="text-sm">Enganche (10%)</span>
                  </div>
                  <p className="text-xl font-bold text-slate-900">{formatPrice(program.price * 0.1)}</p>
                </div>
              </div>

              {program.description && (
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Descripción</h2>
                  <p className="text-slate-600 leading-relaxed">{program.description}</p>
                </div>
              )}

              <div className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Beneficios de financiar con ed.fi</h2>
                <ul className="space-y-3">
                  {[
                    'Aprobación instantánea con decisión en minutos',
                    'Sin aval ni garantía requerida',
                    'Pagos fijos mensuales sin sorpresas',
                    'Proceso 100% digital',
                    'Sin penalización por pago anticipado'
                  ].map((benefit, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-slate-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar - Calculator */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sticky top-24">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Simula tu financiamiento</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Monto a financiar</label>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-2xl font-bold text-brand-blue">
                      {formatPrice(program.price * 0.9)}
                    </p>
                    <p className="text-sm text-slate-500">(90% del programa)</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Plazo</label>
                  <Select value={termMonths} onValueChange={setTermMonths}>
                    <SelectTrigger data-testid="term-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 meses</SelectItem>
                      <SelectItem value="6">6 meses</SelectItem>
                      <SelectItem value="9">9 meses</SelectItem>
                      <SelectItem value="12">12 meses</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {loanCalculation && (
                <div className="bg-brand-blue/5 rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-slate-600">Pago mensual</span>
                    <span className="text-2xl font-bold text-brand-blue" data-testid="monthly-payment">
                      {formatPrice(loanCalculation.monthly_payment)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500">Tasa mensual</span>
                    <span className="text-slate-700">{(loanCalculation.interest_rate_monthly * 100).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span className="text-slate-500">Total a pagar</span>
                    <span className="text-slate-700">{formatPrice(loanCalculation.total_payment)}</span>
                  </div>
                </div>
              )}

              <Button
                className="w-full bg-brand-magenta hover:bg-brand-magenta-dark text-white py-6 text-lg"
                onClick={handleApply}
                data-testid="apply-button"
              >
                Aplicar Ahora
              </Button>

              <p className="text-xs text-center text-slate-500 mt-4">
                Sin compromiso. Decisión en minutos.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations Section */}
      {recommendations.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center gap-2 mb-6">
            <Star className="w-5 h-5 text-brand-magenta" />
            <h2 className="text-2xl font-bold text-slate-900">Programas Similares</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {recommendations.map(rec => (
              <Link
                key={rec.program_id}
                to={`/programs/${rec.program_id}`}
                className="group bg-white rounded-xl border border-slate-200 p-5 card-hover block"
                data-testid={`rec-program-${rec.program_id}`}
              >
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-blue/10 text-brand-blue mb-2">
                  {rec.category}
                </span>
                <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-brand-blue transition-colors">
                  {rec.name}
                </h3>
                <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> {rec.university_name}
                </p>
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <span className="text-xs text-slate-500">{rec.duration_months}m</span>
                  <span className="text-sm font-bold text-brand-blue">{formatPrice(rec.price)}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramDetailPage;
